import React, { useCallback } from 'react';

const BulletinCompetenceTable = ({ bulletin, competences }) => {
	const getNotesForBulletinAndCompetence = useCallback(
		(bulletinEvaluationId, competenceId) => {
			if (bulletin && bulletin.bulletins_evaluation) {
				const evaluation = bulletin.bulletins_evaluation.find(
					(bulletinEval) => bulletinEval.id === bulletinEvaluationId
				);
				if (evaluation) {
					return evaluation.notes_evaluations.find(
						(note) => note.competence === competenceId
					);
				}
			}
			return null; // Return null if no notes are found or if the arrays are empty
		},
		[bulletin]
	);

	const getAverageForBulletinAndCompetence = useCallback(
		(notesTrimestrielles, bulletinId, competenceId) => {
			// Trouver l'entrée correspondante dans notes_trimestrielles pour la compétence donnée et bulletin donné
			const noteTrimestrielle = notesTrimestrielles.find(
				(note) =>
					note.bulletin_trimestriel === bulletinId &&
					note.competence === competenceId
			);

			// Si on trouve une note correspondante, renvoyer les valeurs nécessaires, sinon renvoyer null
			if (noteTrimestrielle) {
				return {
					note_ecrit: noteTrimestrielle.note_ecrit
						? noteTrimestrielle.note_ecrit.toFixed(2)
						: 0,
					note_oral: noteTrimestrielle.note_oral
						? noteTrimestrielle.note_oral.toFixed(2)
						: 0,
					note_pratique: noteTrimestrielle.note_pratique
						? noteTrimestrielle.note_pratique.toFixed(2)
						: 0,
					note_savoir_etre: noteTrimestrielle.note_savoir_etre
						? noteTrimestrielle.note_savoir_etre.toFixed(2)
						: 0,
					note_total: noteTrimestrielle.note_total
						? noteTrimestrielle.note_total.toFixed(2)
						: 0,
					grade: noteTrimestrielle.grade
				};
			}
			return null;
		},
		[]
	);

	const calculateRowSpan = (competence) => {
		let rowCount = 3; // Pour les lignes 'Total' et 'Côte/Appreciation'

		if (competence.points_ecrit !== null) rowCount++;
		if (competence.points_oral !== null) rowCount++;
		if (competence.points_pratique !== null) rowCount++;
		if (competence.points_savoir_etre !== null) rowCount++;

		return rowCount;
	};

	return (
		<div>
			{bulletin && competences && competences.length > 0 ? (
				<>
					<table className="table table-sm table-light table-bordered">
						<thead>
							<tr className="align-middle">
								<th
									className="text-uppercase align-middle fw-bold"
									style={{ width: '43%', color: 'black' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'COMPETENCES'
										: 'COMPETENCE'}
								</th>
								<th
									className="text-uppercase align-middle fw-bold"
									style={{ width: '17%', color: 'black' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'EVALUATIONS'
										: 'EVALUATIONS'}
								</th>
								<th
									className="text-center text-uppercase align-middle fw-bold"
									style={{ color: 'black' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'CI/TL-1'
										: 'M1'}
								</th>
								<th
									className="text-center text-uppercase align-middle fw-bold"
									style={{ color: 'black' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'CI/TL-2'
										: 'M2'}
								</th>
								<th
									className="text-center text-uppercase align-middle fw-bold"
									style={{ color: 'black' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'CI/TL-3'
										: 'M3'}
								</th>
								<th
									className="text-center text-uppercase align-middle fw-bold"
									style={{ color: 'black' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Moy. Gle'
										: 'Overall Avg'}
								</th>
							</tr>
						</thead>
						<tbody>
							{competences.map(
								(competence) =>
									competence && (
										<React.Fragment key={competence.id}>
											<tr>
												<td
													rowSpan={calculateRowSpan(competence)}
													className="text-uppercase align-middle"
												>
													{bulletin.salledeclasse_details.section === 'fr'
														? `${competence.nom} - (${competence.total_points} pts)`
														: `${competence.nom} - (${competence.total_points} mks)`}
												</td>
											</tr>

											{competence.points_oral !== null && (
												<tr>
													<td>
														{bulletin.salledeclasse_details.section === 'fr'
															? `Oral - (${competence.points_oral} pts)`
															: `Oral - (${competence.points_oral} mks)`}
													</td>
													{bulletin.bulletins_evaluation
														.sort((a, b) => a.examen - b.examen)
														.map((bulletin_evaluation, index) => {
															const notes = bulletin
																? getNotesForBulletinAndCompetence(
																		bulletin_evaluation.id,
																		competence.id
																  )
																: null;
															return (
																<td
																	className="text-center"
																	key={
																		bulletin_evaluation
																			? bulletin_evaluation.id
																			: index
																	}
																>
																	{notes ? notes.note_oral : ''}
																</td>
															);
														})}
													{Array.from({
														length: 3 - bulletin.bulletins_evaluation.length
													}).map((_, index) => (
														<td
															className="text-center"
															key={`empty-${index}`}
														></td>
													))}
													<td className="text-center">
														{bulletin.notes_trimestrielles &&
															(() => {
																const notes =
																	getAverageForBulletinAndCompetence(
																		bulletin.notes_trimestrielles,
																		bulletin.id,
																		competence.id
																	);
																return notes ? notes.note_oral : 0;
															})()}
													</td>
												</tr>
											)}

											{competence.points_ecrit !== null && (
												<tr>
													<td>
														{bulletin.salledeclasse_details.section === 'fr'
															? `Ecrit - (${competence.points_ecrit} pts)`
															: `Written - (${competence.points_ecrit} mks)`}
													</td>
													{bulletin.bulletins_evaluation
														.sort((a, b) => a.examen - b.examen)
														.map((bulletin_evaluation, index) => {
															const notes = bulletin
																? getNotesForBulletinAndCompetence(
																		bulletin_evaluation.id,
																		competence.id
																  )
																: null;
															return (
																<td
																	className="text-center"
																	key={
																		bulletin_evaluation
																			? bulletin_evaluation.id
																			: index
																	}
																>
																	{notes ? notes.note_ecrit : ''}
																</td>
															);
														})}
													{Array.from({
														length: 3 - bulletin.bulletins_evaluation.length
													}).map((_, index) => (
														<td
															className="text-center"
															key={`empty-${index}`}
														></td>
													))}
													<td className="text-center">
														{bulletin.notes_trimestrielles &&
															(() => {
																const notes =
																	getAverageForBulletinAndCompetence(
																		bulletin.notes_trimestrielles,
																		bulletin.id,
																		competence.id
																	);
																return notes ? notes.note_ecrit : '';
															})()}
													</td>
												</tr>
											)}

											{competence.points_pratique !== null && (
												<tr>
													<td>
														{bulletin.salledeclasse_details.section === 'fr'
															? `Pratique - (${competence.points_pratique} pts)`
															: `Practical - (${competence.points_pratique} mks)`}
													</td>
													{bulletin.bulletins_evaluation
														.sort((a, b) => a.examen - b.examen)
														.map((bulletin_evaluation, index) => {
															const notes = bulletin
																? getNotesForBulletinAndCompetence(
																		bulletin_evaluation.id,
																		competence.id
																  )
																: null;
															return (
																<td
																	className="text-center"
																	key={
																		bulletin_evaluation
																			? bulletin_evaluation.id
																			: index
																	}
																>
																	{notes ? notes.note_pratique : ''}
																</td>
															);
														})}
													{Array.from({
														length: 3 - bulletin.bulletins_evaluation.length
													}).map((_, index) => (
														<td
															className="text-center"
															key={`empty-${index}`}
														></td>
													))}
													<td className="text-center">
														{bulletin.notes_trimestrielles &&
															(() => {
																const notes =
																	getAverageForBulletinAndCompetence(
																		bulletin.notes_trimestrielles,
																		bulletin.id,
																		competence.id
																	);
																return notes ? notes.note_pratique : '';
															})()}
													</td>
												</tr>
											)}

											{competence.points_savoir_etre !== null && (
												<tr>
													<td>
														{bulletin.salledeclasse_details.section === 'fr'
															? `Savoir-être - (${competence.points_savoir_etre} pts)`
															: `Attitude - (${competence.points_savoir_etre} mks)`}
													</td>
													{bulletin.bulletins_evaluation
														.sort((a, b) => a.examen - b.examen)
														.map((bulletin_evaluation, index) => {
															const notes = bulletin
																? getNotesForBulletinAndCompetence(
																		bulletin_evaluation.id,
																		competence.id
																  )
																: null;
															return (
																<td
																	className="text-center"
																	key={
																		bulletin_evaluation
																			? bulletin_evaluation.id
																			: index
																	}
																>
																	{notes ? notes.note_savoir_etre : ''}
																</td>
															);
														})}
													{Array.from({
														length: 3 - bulletin.bulletins_evaluation.length
													}).map((_, index) => (
														<td
															className="text-center"
															key={`empty-${index}`}
														></td>
													))}
													<td className="text-center">
														{bulletin.notes_trimestrielles &&
															(() => {
																const notes =
																	getAverageForBulletinAndCompetence(
																		bulletin.notes_trimestrielles,
																		bulletin.id,
																		competence.id
																	);
																return notes ? notes.note_savoir_etre : '';
															})()}
													</td>
												</tr>
											)}

											<tr
												className="fw-bold"
												style={{ backgroundColor: '#b1b1b1' }}
											>
												<td>Total</td>
												{bulletin.bulletins_evaluation
													.sort((a, b) => a.examen - b.examen)
													.map((bulletin_evaluation, index) => {
														const notes = bulletin
															? getNotesForBulletinAndCompetence(
																	bulletin_evaluation.id,
																	competence.id
															  )
															: null;
														return (
															<td
																className="text-center"
																key={bulletin ? bulletin_evaluation.id : index}
															>
																{notes ? notes.note_total : ''}
															</td>
														);
													})}
												{Array.from({
													length: 3 - bulletin.bulletins_evaluation.length
												}).map((_, index) => (
													<td
														className="text-center"
														key={`empty-${index}`}
													></td>
												))}
												<td className="text-center">
													{bulletin.notes_trimestrielles &&
														(() => {
															const notes = getAverageForBulletinAndCompetence(
																bulletin.notes_trimestrielles,
																bulletin.id,
																competence.id
															);
															return notes ? notes.note_total : '';
														})()}
												</td>
											</tr>

											<tr className="fw-bold">
												<td>
													{bulletin.salledeclasse_details.section === 'fr'
														? 'Côte'
														: 'Appreciation'}
												</td>
												{bulletin.bulletins_evaluation
													.sort((a, b) => a.examen - b.examen)
													.map((bulletin_evaluation, index) => {
														const notes = bulletin
															? getNotesForBulletinAndCompetence(
																	bulletin_evaluation.id,
																	competence.id
															  )
															: null;
														return (
															<td
																className="text-center"
																key={bulletin ? bulletin_evaluation.id : index}
															>
																{notes ? notes.grade : ''}
															</td>
														);
													})}
												{Array.from({
													length: 3 - bulletin.bulletins_evaluation.length
												}).map((_, index) => (
													<td
														className="text-center"
														key={`empty-${index}`}
													></td>
												))}
												<td
													className="text-center"
													style={{ backgroundColor: '#b1b1b1' }}
												>
													{bulletin.notes_trimestrielles &&
														(() => {
															const notes = getAverageForBulletinAndCompetence(
																bulletin.notes_trimestrielles,
																bulletin.id,
																competence.id
															);
															return notes ? notes.grade : '';
														})()}
												</td>
											</tr>
										</React.Fragment>
									)
							)}
						</tbody>
					</table>

					<table className="table table-sm table-light table-bordered mt-4">
						<thead>
							<tr className="align-middle">
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'DISCIPLINE'
										: 'SUBJECT'}
								</th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Absences'
										: 'Abscences'}
								</th>
								<th style={{ width: '50px' }}></th>
								<th colSpan="2">
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Heures justifiées'
										: 'Justified Hours'}
								</th>
								<th></th>
								<th colSpan="2">
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Heures non justifiées'
										: 'Unjustified Hours'}
								</th>
								<th></th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Jours suspension'
										: 'Suspension Days'}
								</th>
								<th style={{ width: '50px' }}></th>
							</tr>
							<tr className="align-middle">
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'STATS DE PERFORMANCES'
										: 'PERFORMANCE STATS'}
								</th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? "Moyenne de l'élève"
										: "Pupil's Average"}
								</th>
								<th style={{ width: '50px' }} className="text-center">
									{bulletin.moyenne_generale !== null &&
									bulletin.moyenne_generale !== undefined
										? bulletin.moyenne_generale.toFixed(2)
										: ''}
								</th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Moyenne Premier'
										: 'First Average'}
								</th>
								<th style={{ width: '50px' }} className="text-center">
									{bulletin.moyenne_premier !== null &&
									bulletin.moyenne_premier !== undefined
										? bulletin.moyenne_premier.toFixed(2)
										: ''}
								</th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Moyenne Dernier'
										: 'Last Average'}
								</th>
								<th style={{ width: '50px' }} className="text-center">
									{bulletin.moyenne_dernier !== null &&
									bulletin.moyenne_dernier !== undefined
										? bulletin.moyenne_dernier.toFixed(2)
										: ''}
								</th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Moyenne Classe'
										: 'Class Average'}
								</th>
								<th style={{ width: '50px' }} className="text-center">
									{bulletin.moyenne_classe !== null &&
									bulletin.moyenne_classe !== undefined
										? bulletin.moyenne_classe.toFixed(2)
										: ''}
								</th>
								<th>
									{bulletin.salledeclasse_details.section === 'fr'
										? '% Réussite'
										: '% Success'}
								</th>
								<th style={{ width: '50px' }} className="text-center">
									{bulletin.pourcentage_reussite !== null &&
									bulletin.pourcentage_reussite !== undefined
										? bulletin.pourcentage_reussite.toFixed(2)
										: ''}
									%
								</th>
							</tr>
						</thead>
						<tbody>
							<tr className="align-middle">
								<th
									colSpan="11"
									className="text-center text-dark fw-bold"
									style={{ backgroundColor: '#b1b1b1' }}
								>
									{bulletin.salledeclasse_details.section === 'fr'
										? 'OBSERVATION'
										: 'OBSERVATION'}
								</th>
							</tr>
							<tr className="align-middle">
								<th colSpan="2">
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Observations du titulaire'
										: "Teacher's Remarks"}
								</th>
								<th colSpan="3">
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Observations disciplinaires'
										: 'Appreciations'}
								</th>
								<th colSpan="3">
									{bulletin.salledeclasse_details.section === 'fr'
										? 'Signature des parents'
										: "Parent's Signature"}
								</th>
								<th colSpan="3">
									{bulletin.salledeclasse_details.section === 'fr'
										? "Signature du chef de l'établissement"
										: "Headteacher's Signature"}
								</th>
							</tr>
							<tr>
								<td colSpan="2" style={{ height: '120px' }}></td>
								<td colSpan="3" style={{ height: '120px' }}></td>
								<td colSpan="3" style={{ height: '120px' }}></td>
								<td colSpan="3" style={{ height: '120px' }}></td>
							</tr>
						</tbody>
					</table>
				</>
			) : (
				<div>
					Chargement des notes de l'élève pour le bulletin trimestriel (Bulletin
					Competences)...
				</div>
			)}
		</div>
	);
};

export default BulletinCompetenceTable;
